/**
 * Catálogo compartido de campos y operadores del buscador.
 *
 * Antes esto vivía duplicado literalmente en cuatro lugares: `OPERATORS_CONFIG` y
 * `getInputType` en `FilterBuilder` y en `AdvancedFilterBuilder`, y el switch que arma
 * la condición del payload dos veces dentro de `Buscador`. Agregar un tipo de campo
 * exigía tocar los cuatro y acertarle a los cuatro.
 */

/**
 * Campos del sistema que se pueden filtrar además de los metadatos de la serie.
 *
 * Los metadatos son propios de cada serie (ADR-021, van scopeados bajo
 * `metadata.<idTipoDoc>.<campo>`); la firma es del documento, así que estos campos
 * viven en el mismo catálogo que `created_at` o `status` del lado del servidor.
 *
 * `signed` no existe como campo en el índice: el backend lo resuelve por presencia o
 * ausencia de la fecha de firma (operadores `exists`/`not_exists`).
 */
export const SYSTEM_SEARCH_FIELDS = [
  // Identificación del documento
  { name: 'filename', label: 'Nombre de archivo', type: 'STRING', system: true },
  { name: 'size', label: 'Tamaño (KB)', type: 'INTEGER', system: true, scale: 1024 },
  // Importación
  { name: 'createdAt', label: 'Fecha de importación', type: 'DATE', system: true },
  { name: 'createdBy', label: 'Importado por', type: 'STRING', system: true },
  // Revisión y aprobación
  { name: 'reviewedBy', label: 'Revisado por', type: 'STRING', system: true },
  { name: 'reviewedAt', label: 'Fecha de revisión', type: 'DATE', system: true },
  { name: 'approvedBy', label: 'Aprobado por', type: 'STRING', system: true },
  { name: 'approvedAt', label: 'Fecha de aprobación', type: 'DATE', system: true },
  // Firma
  { name: 'signed', label: 'Está firmado', type: 'BOOLEAN', system: true },
  { name: 'signedBy', label: 'Firmado por', type: 'STRING', system: true },
  { name: 'signedAt', label: 'Fecha de firma', type: 'DATE', system: true },
];

/**
 * Campos de rechazo. Viven aparte de {@link SYSTEM_SEARCH_FIELDS} a propósito: sólo tienen
 * valor en la bandeja de Rechazados de Control de Calidad. En el árbol documental no aparece
 * ningún documento rechazado, así que ofrecerlos ahí sería otro filtro que no puede encontrar
 * nada — exactamente lo que este cambio corrige.
 */
const REJECTION_FIELDS = [
  { name: 'rejectedBy', label: 'Rechazado por', type: 'STRING', system: true },
  { name: 'rejectedAt', label: 'Fecha de rechazo', type: 'DATE', system: true },
];

/** Índice por nombre sobre todo el catálogo, para armar los subconjuntos por estado. */
const CATALOGO_CAMPOS = Object.fromEntries(
  [...SYSTEM_SEARCH_FIELDS, ...REJECTION_FIELDS].map((f) => [f.name, f]),
);

/**
 * Los cuatro campos que **todo documento tiene**, sin importar en qué estado esté. Son los
 * únicos que siempre se pueden ofrecer.
 */
const SIEMPRE = ['filename', 'size', 'createdAt', 'createdBy'];

/**
 * Campos extra por pestaña de Control de Calidad, además de los cuatro de siempre.
 *
 * El criterio es el circuito del documento —OCR → Revisión → Aprobación → Firma → Publicado
 * (`WorkflowService`)— y el hecho de que cada pestaña **fuerza su estado del lado del
 * servidor** (`SearchDocsRequest`): lo que ya ocurrió tiene valor, lo que falta no.
 *
 * Los campos de firma no figuran en ninguna: un documento firmado pasa a `PUBLISHED` y sale de
 * Control de Calidad, así que "está firmado = sí" nunca devolvería nada y "= no" no filtraría
 * nada. Es la razón por la que ADR-040 dejó esta pantalla fuera de alcance.
 */
const EXTRA_POR_ESTADO = {
  ocr: [],
  review: [],
  approval: ['reviewedBy', 'reviewedAt'],
  signature: ['reviewedBy', 'reviewedAt'],
  rejected: ['rejectedBy', 'rejectedAt'],
};

/**
 * Campos del sistema que corresponde ofrecer en una pestaña de Control de Calidad.
 *
 * @param tab              `ocr` · `review` · `approval` · `signature` · `rejected`
 * @param approvalRequired si la serie exige aprobación. Sólo importa en la pestaña de firma:
 *                         cuando la serie **no** la exige, el documento pasa de revisión
 *                         directo a firma y los campos de aprobación quedan vacíos.
 * @param reviewRequired   si la serie exige revisión. Cuando **no** la exige,
 *                         `WorkflowService.handleImportingStatus` manda el documento directo a
 *                         aprobación o a firma sin pasar por revisión, y `reviewed_by` /
 *                         `reviewed_at` quedan NULL para siempre. Ofrecerlos ahí es un filtro
 *                         que no puede encontrar nada — el mismo defecto que este catálogo
 *                         existe para evitar. El default es `true` porque quitarle campos a un
 *                         llamador que no lo pasa sería una regresión silenciosa; las pantallas
 *                         pasan el valor real de la serie.
 * @returns las specs completas, en el orden en que se muestran
 */
export const systemFieldsForTab = (tab, { approvalRequired = false, reviewRequired = true } = {}) => {
  const extra = (EXTRA_POR_ESTADO[tab] || [])
    .filter((name) => reviewRequired || !name.startsWith('reviewed'));

  const conAprobacion = tab === 'signature' && approvalRequired
    ? [...extra, 'approvedBy', 'approvedAt']
    : extra;

  return [...SIEMPRE, ...conAprobacion].map((name) => CATALOGO_CAMPOS[name]).filter(Boolean);
};

/**
 * Campos del **paso inmediatamente anterior** al estado de la pestaña: lo que el usuario está
 * mirando cuando abre esa bandeja. Es lo que se muestra por defecto en la grilla.
 *
 * Vive acá y no en el catálogo de columnas para que la regla del circuito esté escrita **una
 * sola vez**: cuál es el paso anterior depende de la configuración de la serie, y esa lógica ya
 * gobierna qué campos se pueden filtrar.
 *
 * Devuelve vacío cuando no hubo ningún paso anterior con dato propio —OCR y revisión, o una
 * serie que no exige ni revisión ni aprobación—; en ese caso la grilla cae a los campos de la
 * importación, que todo documento tiene.
 */
export const camposDelPasoAnterior = (tab, { approvalRequired = false, reviewRequired = true } = {}) => {
  if (tab === 'rejected') return ['rejectedBy', 'rejectedAt'];
  if (tab === 'signature' && approvalRequired) return ['approvedBy', 'approvedAt'];
  if ((tab === 'approval' || tab === 'signature') && reviewRequired) return ['reviewedBy', 'reviewedAt'];
  return [];
};

/** Operadores disponibles según el tipo de campo. */
export const OPERATORS_CONFIG = {
  STRING: [
    { value: 'term', label: 'Igual a', inputs: 1 },
    { value: 'wildcard_contains', label: 'Contiene', inputs: 1 },
    { value: 'match', label: 'Contiene palabras', inputs: 1 },
    { value: 'wildcard_starts', label: 'Empieza con', inputs: 1 },
    { value: 'wildcard_ends', label: 'Termina con', inputs: 1 },
    { value: 'fuzzy', label: 'Similar a', inputs: 1 },
  ],
  INTEGER: [
    { value: 'term', label: 'Igual a', inputs: 1 },
    { value: 'range_gt', label: 'Mayor que', inputs: 1 },
    { value: 'range_lt', label: 'Menor que', inputs: 1 },
    { value: 'range_between', label: 'Entre', inputs: 2 },
  ],
  LONG: [
    { value: 'term', label: 'Igual a', inputs: 1 },
    { value: 'range_gt', label: 'Mayor que', inputs: 1 },
    { value: 'range_lt', label: 'Menor que', inputs: 1 },
    { value: 'range_between', label: 'Entre', inputs: 2 },
  ],
  DATE: [
    { value: 'term', label: 'Igual a', inputs: 1 },
    { value: 'range_after', label: 'Después de', inputs: 1 },
    { value: 'range_before', label: 'Antes de', inputs: 1 },
    { value: 'range_between', label: 'Entre', inputs: 2 },
  ],
  BOOLEAN: [
    { value: 'boolean_is', label: 'Es', inputs: 1 },
  ],
};

/**
 * Opciones del desplegable de un campo booleano.
 *
 * Los valores son los strings `'true'`/`'false'` a propósito: `Buscador` descarta
 * cualquier filtro con valor falsy (`if (!filter.value) return`) y calcula
 * `value.trim()` para habilitar el botón de buscar. Un `false` booleano se perdería
 * en silencio en ambos lugares.
 */
export const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'Sí' },
  { value: 'false', label: 'No' },
];

/**
 * Convierte el día que eligió el usuario a uno de los extremos de ese día, en ISO UTC.
 *
 * Bóveda guarda y consulta en UTC, y la interfaz convierte en los dos sentidos (ADR-041):
 * la grilla muestra en la zona del navegador, así que **el filtro tiene que preguntar por el
 * día de esa misma zona**. Si no, no coinciden: con los límites en UTC, un usuario en GMT-3
 * que filtraba por el "18 de junio" pedía en realidad de las 21:00 del 17 a las 20:59 del 18,
 * hora local. Una firma de las 22:00 no aparecía bajo la fecha con la que figuraba en la
 * grilla.
 *
 * El día se parsea a mano en vez de con `new Date('2026-06-18')`, porque ese constructor
 * interpreta la fecha sola como medianoche **UTC** y volvería a introducir el corrimiento.
 *
 * @param dateString `YYYY-MM-DD` del selector, o un valor que ya traiga hora
 * @param bound      `'start'` → 00:00:00.000 local · `'end'` → 23:59:59.999 local
 * @returns el instante en ISO-8601 UTC, o `null` si no hay valor
 */
export const formatDateToISO = (dateString, bound = 'start') => {
  if (!dateString) return null;

  // Si ya trae hora (datetime-local), se respeta lo que vino.
  const hasTime = typeof dateString === 'string' && /T\d{2}:\d{2}/.test(dateString);
  if (hasTime) return new Date(dateString).toISOString();

  const [year, month, day] = String(dateString).split('-').map(Number);
  const date = bound === 'end'
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day, 0, 0, 0, 0);

  return date.toISOString();
};

/** Busca la spec de un campo por nombre dentro del catálogo dado. */
export const findField = (fields, fieldName) =>
  (fields || []).find((f) => f.name === fieldName);

/**
 * Separa el catálogo en campos del sistema y metadatos de la serie, para que el
 * desplegable los muestre agrupados. Con once campos de sistema, una lista plana
 * mezclada con los metadatos de la serie no deja distinguir qué es cada cosa.
 * Devuelve sólo los grupos que tienen contenido.
 */
export const groupFields = (fields) => {
  const system = (fields || []).filter((f) => f.system);
  const metadata = (fields || []).filter((f) => !f.system);
  const groups = [];
  if (system.length) groups.push({ label: 'Campos del sistema', fields: system });
  if (metadata.length) groups.push({ label: 'Metadatos de la serie', fields: metadata });
  return groups;
};

/** Operadores disponibles para un campo del catálogo. */
export const getOperatorsForField = (fields, fieldName) => {
  const field = findField(fields, fieldName);
  if (!field) return [];
  return OPERATORS_CONFIG[field.type] || [];
};

/** Cantidad de inputs que necesita un operador (2 para los rangos). */
export const getInputCount = (fields, fieldName, operatorValue) => {
  const operators = getOperatorsForField(fields, fieldName);
  return operators.find((op) => op.value === operatorValue)?.inputs || 1;
};

/** Tipo de `<input>` HTML para un campo. */
export const getInputType = (fields, fieldName) => {
  const field = findField(fields, fieldName);
  if (!field) return 'text';

  switch (field.type) {
    case 'INTEGER':
    case 'LONG':
      return 'number';
    case 'DATE':
      return 'date';
    default:
      return 'text';
  }
};

/**
 * Describe con qué control se edita el valor de un campo.
 * Los booleanos usan un desplegable Sí/No; el resto, un input.
 */
export const getFieldControl = (fields, fieldName) => {
  const field = findField(fields, fieldName);
  if (field?.type === 'BOOLEAN') {
    return { kind: 'select', options: BOOLEAN_OPTIONS };
  }
  return { kind: 'input', type: getInputType(fields, fieldName) };
};

/**
 * Traduce un filtro de la UI a la condición que espera el API.
 *
 * @param filter    `{ fieldName, operator, value, value2 }` del builder
 * @param field     spec del campo (para conocer su `type`)
 * @param toISO     expande un día a sus extremos; por default el helper compartido de acá.
 *                  Sigue siendo un parámetro para que los tests puedan inyectar otro.
 * @returns la condición, o `null` si el filtro está incompleto y hay que descartarlo
 */
export const buildCondition = (filter, field, toISO = formatDateToISO) => {
  const condition = { field: filter.fieldName };

  // Algunos campos se muestran en una unidad y se almacenan en otra: el tamaño se
  // pide en KB —igual que la columna de la grilla— y viaja en bytes. Sin esto, quien
  // filtre "mayor a 100" pensando en KB recibe prácticamente todo.
  const num = (v) => Number(v) * (field.scale || 1);

  switch (filter.operator) {
    case 'term':
      // "Igual a" sobre una fecha significa "ese día", no "ese instante". El selector
      // entrega 2026-06-18 y el dato guardado tiene hora, así que una igualdad literal
      // compara contra la medianoche exacta y **no matchea nunca**. Sobre los campos del
      // sistema ni siquiera llega a no matchear: el formato declarado exige fecha y hora,
      // el valor no parsea y la búsqueda falla entera. Se traduce al día completo, que es
      // lo que el usuario quiso pedir. Mismo criterio que "Entre" con la misma fecha en
      // ambos extremos.
      if (field.type === 'DATE') {
        condition.operator = 'range';
        condition.gte = toISO(filter.value, 'start');
        condition.lte = toISO(filter.value, 'end');
        break;
      }

      // Un campo escalado se muestra **redondeado**: la grilla calcula
      // `Math.round(size / 1024)`. Multiplicar el valor tipeado por la escala apunta al
      // centro de ese redondeo, no al intervalo, así que la igualdad sólo acertaba cuando
      // el archivo pesaba un múltiplo exacto de 1024 bytes — 1 caso de cada 1024. El
      // documento del issue #46 pesa 2.281.220 bytes, se muestra como 2228 KB y el filtro
      // buscaba 2.281.472: 252 bytes de diferencia, y no lo encontraba nunca.
      //
      // Es el mismo criterio que arriba con las fechas: preguntar por "igual a" un valor
      // que se muestra redondeado significa **todo lo que se muestra así**.
      // `Math.round(v / S) === n`  ⟺  `v ∈ [n*S - S/2 , n*S + S/2 - 1]`.
      if (field.scale) {
        const centro = num(filter.value);
        const medio = field.scale / 2;
        condition.operator = 'range';
        condition.gte = centro - medio;
        condition.lte = centro + medio - 1;
        break;
      }

      condition.operator = 'term';
      condition.value = filter.value;
      break;

    case 'match':
      condition.operator = 'match';
      condition.value = filter.value;
      break;

    case 'wildcard_contains':
      condition.operator = 'wildcard';
      condition.value = `*${filter.value}*`;
      break;

    case 'wildcard_starts':
      condition.operator = 'wildcard';
      condition.value = `${filter.value}*`;
      break;

    case 'wildcard_ends':
      condition.operator = 'wildcard';
      condition.value = `*${filter.value}`;
      break;

    case 'fuzzy':
      condition.operator = 'fuzzy';
      condition.value = filter.value;
      break;

    // Un booleano derivado se pregunta por la presencia del campo, no por su valor:
    // "está firmado" es "tiene fecha de firma".
    case 'boolean_is':
      condition.operator = filter.value === 'true' ? 'exists' : 'not_exists';
      break;

    case 'range_gt':
      condition.operator = 'range';
      if (field.type === 'DATE') {
        // "Después de X" → estricto sobre fin del día X (X+1 inclusive).
        condition.gt = toISO(filter.value, 'end');
      } else {
        condition.gt = num(filter.value);
      }
      break;

    case 'range_lt':
      condition.operator = 'range';
      if (field.type === 'DATE') {
        // "Antes de X" → estricto sobre inicio del día X.
        condition.lt = toISO(filter.value, 'start');
      } else {
        condition.lt = num(filter.value);
      }
      break;

    case 'range_after':
      condition.operator = 'range';
      condition.gt = toISO(filter.value, 'end');
      break;

    case 'range_before':
      condition.operator = 'range';
      condition.lt = toISO(filter.value, 'start');
      break;

    case 'range_between':
      if (!filter.value2) return null; // Requiere ambos valores
      condition.operator = 'range';
      if (field.type === 'DATE') {
        // Cubrimos el día completo en ambos extremos: gte = inicio del día del 1er
        // valor, lte = fin del día del 2do. Esto soluciona el bug en el que
        // "BETWEEN X AND X" devolvía vacío porque los timestamps de los docs caían
        // dentro del día pero no en 00:00:00 UTC exacto.
        condition.gte = toISO(filter.value, 'start');
        condition.lte = toISO(filter.value2, 'end');
      } else {
        condition.gte = num(filter.value);
        condition.lte = num(filter.value2);
      }
      break;

    default:
      return null;
  }

  return condition;
};
